import { useHeaderHeight } from '@react-navigation/elements';
import * as Linking from 'expo-linking';
import { Redirect, router } from 'expo-router';
import * as React from 'react';
import {
    type LegacyRef,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { KeyboardAvoidingView, Platform, type TextInput } from 'react-native';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H5Regular,
    Input,
    isWeb,
    SafeAreaView,
    YStack,
} from 'smoothie';

import { isEmail } from '../../../common/misc';
import { getWebUrl } from '../../../lib/Web';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../../logger/Logger';
import { startForgotPassword } from '../../api/AuthApi';

const ForgotPasswordStart = () => {
    LogRocketFacade.tagPage('ForgotPasswordStart');

    const headerHeight = useHeaderHeight();

    const [submitEnabled, setSubmitEnabled] = useState(false);

    const emailInput: LegacyRef<TextInput> | null = useRef(null);

    const emailValue = useRef('');

    const setEmail = useCallback((value: string) => {
        emailValue.current = value.trim();
        setSubmitEnabled(isEmail(emailValue.current));
    }, []);

    const submitEmail = async () => {
        setSubmitEnabled(false);
        const email = emailValue.current;

        try {
            await startForgotPassword({
                email,
            });
        } catch (e) {
            captureException(e, { email });
        } finally {
            setSubmitEnabled(true);
            router.navigate('/sign-in/forgot-password/finish');
        }
    };

    // setting a small timeout here so the keyboard doesn't show up until after the stack transition
    // otherwise the help sheet on the previous screen animates out of the way first and it looks janky
    useEffect(() => {
        setTimeout(() => emailInput.current?.focus(), 100);
    }, [emailInput]);

    // Currently encountering inconsistent behavior between mobile mail clients and redirects to
    // the in-app password reset flow. For now, we are redirecting the user to reset their password in the web app instead.
    if (!isWeb) {
        const url = `${getWebUrl()}/sign-in/forgot-password`;
        void Linking.openURL(url);
        return <Redirect href="/" />;
    }

    return (
        <SafeAreaView style={{ flex: 1 }}>
            <KeyboardAvoidingView
                style={{
                    flex: 1,
                    height: '100%',
                    ...(isWeb && {
                        justifyContent: 'center',
                        alignItems: 'center',
                    }),
                }}
                behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                keyboardVerticalOffset={headerHeight + 16}
            >
                <YStack
                    paddingHorizontal={20}
                    flex={1}
                    justifyContent="center"
                    gap={24}
                >
                    <YStack>
                        <H5Regular marginBottom={8}>
                            Need to reset your password?
                        </H5Regular>
                        <BodySmallRegular marginBottom={12} color="$gray1000">
                            We&apos;ll email you a link to help
                        </BodySmallRegular>
                        <Input
                            ref={emailInput}
                            autoCapitalize="none"
                            height={54}
                            padding={16}
                            borderRadius={4}
                            borderColor="$gray200"
                            key="email"
                            placeholder="Email"
                            autoComplete="email"
                            inputMode="email"
                            keyboardType="email-address"
                            textContentType="username"
                            defaultValue={emailValue.current}
                            onChangeText={setEmail}
                            onSubmitEditing={submitEmail}
                            blurOnSubmit={false}
                        />
                    </YStack>

                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Recover"
                        onPress={submitEmail}
                        disabled={!submitEnabled}
                    />
                </YStack>
            </KeyboardAvoidingView>
        </SafeAreaView>
    );
};

export default ForgotPasswordStart;
